
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import LoginForm from "@/components/auth/LoginForm";
import ExpiredLinkMessage from "@/components/auth/ExpiredLinkMessage";

const Login = () => {
  const navigate = useNavigate();
  const [showExpiredMessage, setShowExpiredMessage] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkSession = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // First, check if there's already an active session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError) {
          console.error('Session check failed:', sessionError.message);
          await supabase.auth.signOut();
          return;
        }
        
        if (session) {
          console.log('Active session found, redirecting to dashboard');
          navigate('/dashboard');
          return;
        }

        // Check URL parameters for type=recovery
        const params = new URLSearchParams(window.location.hash.substring(1));
        const type = params.get('type');
        
        if (type === 'recovery') {
          console.log('Password reset token found, redirecting to password reset page');
          // Clear the URL fragment after processing
          window.history.replaceState(null, '', window.location.pathname);
          navigate('/password-reset');
          return;
        }

        // Check for magic link authentication in URL
        const accessToken = params.get('access_token');
        const refreshToken = params.get('refresh_token');
        
        console.log('Checking for magic link tokens:', { 
          hasAccessToken: !!accessToken, 
          hasRefreshToken: !!refreshToken 
        });
        
        if (accessToken && refreshToken) {
          console.log('Magic link tokens found, setting session...');
          try {
            const { data: { session }, error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken
            });
            
            if (error) {
              console.error('Error setting session:', error.message);
              if (error.message.includes('expired')) {
                setShowExpiredMessage(true);
              }
              return;
            }
            
            if (session) {
              console.log('Session set successfully, redirecting to dashboard');
              // Clear the URL fragment after processing
              window.history.replaceState(null, '', window.location.pathname);
              navigate('/dashboard');
              return;
            }
          } catch (e) {
            console.error('Failed to set session:', e);
            setError('Failed to authenticate. Please try again.');
          }
        }

        // Check for expired token error in URL
        const error = params.get('error');
        const errorDescription = params.get('error_description');
        
        if (error === 'access_denied' && errorDescription?.includes('expired')) {
          console.log('Password reset link has expired');
          setShowExpiredMessage(true);
          // Clear the URL fragment after processing
          window.history.replaceState(null, '', window.location.pathname);
          return;
        }
      } catch (error) {
        console.error('Session check error:', error);
        setError('An unexpected error occurred. Please try again later.');
        await supabase.auth.signOut();
      } finally {
        setIsLoading(false);
      }
    };

    checkSession();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth state changed:', event, session?.user?.email);
      
      if (event === 'SIGNED_IN' && session) {
        console.log('User signed in, redirecting to dashboard');
        navigate('/dashboard');
        return;
      }

      if (event === 'SIGNED_OUT') {
        console.log('User signed out');
        navigate('/login');
        return;
      }
    });

    // Set a timeout to prevent infinite loading state
    const timeoutId = setTimeout(() => {
      setIsLoading(false);
    }, 5000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeoutId);
    };
  }, [navigate]);

  // Simple fallback for network errors
  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 relative">
        <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg">
          <div className="text-center">
            <h2 className="mt-6 text-2xl font-bold text-gray-900">Connection Error</h2>
            <p className="mt-2 text-sm text-gray-600">{error}</p>
            <button 
              onClick={() => window.location.reload()} 
              className="mt-4 w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 relative">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg">
        {isLoading ? (
          <div className="text-center py-8">
            <div className="inline-block w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-gray-600">Loading...</p>
          </div>
        ) : showExpiredMessage ? (
          <ExpiredLinkMessage onEmailSent={() => setShowExpiredMessage(false)} />
        ) : (
          <LoginForm />
        )}
      </div>
    </div>
  );
};

export default Login;
